import { createContext, useContext } from 'react';
import isEqual from 'lodash.isequal';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { devtools } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createStore, type StateCreator, type StoreApi } from 'zustand/vanilla';

import type { GroupEntryView } from '~/services/marketplace/marketplace-service.schemas';

// The pending card keys on a `pending:`-namespaced variant: the two cards have opposite
// expansion defaults, so sharing one toggle bit would invert the choice across pending -> settled.
const PENDING_CARD_KEY_PREFIX = 'pending:';
export const pendingGroupEntryExpansionKey = (entryId: string) =>
  `${PENDING_CARD_KEY_PREFIX}${entryId}`;
const expansionKeyEntryId = (key: string) =>
  key.startsWith(PENDING_CARD_KEY_PREFIX) ? key.slice(PENDING_CARD_KEY_PREFIX.length) : key;

export type GroupEntryViewsState = {
  viewsByEntryId: ReadonlyMap<string, GroupEntryView>;
  expandedEntryIds: ReadonlySet<string>;
  isLoading: boolean;
  isError: boolean;
  setViews: (viewsByEntryId: ReadonlyMap<string, GroupEntryView>) => void;
  setStatus: (isLoading: boolean, isError: boolean) => void;
  toggleExpanded: (expansionKey: string) => void;
  pruneExpanded: (entryIds: ReadonlySet<string>) => void;
  reset: () => void;
};

const initialState: Pick<
  GroupEntryViewsState,
  'viewsByEntryId' | 'expandedEntryIds' | 'isLoading' | 'isError'
> = {
  viewsByEntryId: new Map<string, GroupEntryView>(),
  expandedEntryIds: new Set<string>(),
  isLoading: false,
  isError: false,
};

const createGroupEntryViewsState: StateCreator<
  GroupEntryViewsState,
  [['zustand/devtools', never]]
> = (set) => ({
  ...initialState,
  setViews: (viewsByEntryId) =>
    set(
      (state) => {
        let changed = state.viewsByEntryId.size !== viewsByEntryId.size;
        const nextViews = new Map<string, GroupEntryView>();

        viewsByEntryId.forEach((view, entryId) => {
          const previousView = state.viewsByEntryId.get(entryId);
          const nextView = previousView && isEqual(previousView, view) ? previousView : view;
          nextViews.set(entryId, nextView);
          if (nextView !== previousView) changed = true;
        });

        return changed ? { viewsByEntryId: nextViews } : state;
      },
      false,
      'setViews'
    ),
  setStatus: (isLoading, isError) =>
    set(
      (state) =>
        state.isLoading === isLoading && state.isError === isError ? state : { isLoading, isError },
      false,
      'setStatus'
    ),
  toggleExpanded: (expansionKey) =>
    set(
      (state) => {
        const expandedEntryIds = new Set(state.expandedEntryIds);
        if (expandedEntryIds.has(expansionKey)) expandedEntryIds.delete(expansionKey);
        else expandedEntryIds.add(expansionKey);
        return { expandedEntryIds };
      },
      false,
      'toggleExpanded'
    ),
  pruneExpanded: (entryIds) =>
    set(
      (state) => {
        const expandedEntryIds = new Set(
          [...state.expandedEntryIds].filter((key) => entryIds.has(expansionKeyEntryId(key)))
        );
        return expandedEntryIds.size === state.expandedEntryIds.size ? state : { expandedEntryIds };
      },
      false,
      'pruneExpanded'
    ),
  reset: () => set(initialState, false, 'reset'),
});

export type GroupEntryViewsStore = StoreApi<GroupEntryViewsState>;

export const createGroupEntryViewsStore = (): GroupEntryViewsStore =>
  createStore<GroupEntryViewsState>()(
    devtools(createGroupEntryViewsState, { name: 'GroupEntryViewsStore' })
  );

// Null context = no provider. Reads of this module-level singleton are harmless (empty, not
// loading), but writes must never reach it, so write hooks hand out a no-op instead.
const inertGroupEntryViewsStore = createGroupEntryViewsStore();

export const GroupEntryViewsStoreContext = createContext<GroupEntryViewsStore | null>(null);

export const useGroupEntryViewsStatus = () => {
  const store = useContext(GroupEntryViewsStoreContext) ?? inertGroupEntryViewsStore;
  return useStoreWithEqualityFn(
    store,
    (state) => ({ isLoading: state.isLoading, isError: state.isError }),
    shallow
  );
};

export const useGroupEntryViewSelector = (entryId: string) => {
  const store = useContext(GroupEntryViewsStoreContext) ?? inertGroupEntryViewsStore;
  return useStoreWithEqualityFn(store, (state) => state.viewsByEntryId.get(entryId));
};

// Membership means toggled-from-default, not expanded; defaultExpanded inverts the read.
export const useGroupEntryExpanded = (expansionKey: string, defaultExpanded = false) => {
  const store = useContext(GroupEntryViewsStoreContext) ?? inertGroupEntryViewsStore;
  const toggled = useStoreWithEqualityFn(store, (state) =>
    state.expandedEntryIds.has(expansionKey)
  );
  return defaultExpanded ? !toggled : toggled;
};

const noopToggleExpanded: GroupEntryViewsState['toggleExpanded'] = () => {};

export const useToggleGroupEntryExpanded = () => {
  const store = useContext(GroupEntryViewsStoreContext);
  const toggleExpanded = useStoreWithEqualityFn(
    store ?? inertGroupEntryViewsStore,
    (state) => state.toggleExpanded
  );
  return store ? toggleExpanded : noopToggleExpanded;
};
